import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Mail, 
  MessageCircle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye,
  Download,
  FileText,
  RefreshCw,
  Filter,
  BarChart3,
  Send,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // WhatsApp Analytics & Alerts
  const [whatsappAnalytics, setWhatsappAnalytics] = useState(null);
  const [whatsappAlerts, setWhatsappAlerts] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Filters
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    context: '',
    startDate: '',
    endDate: ''
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.context) params.append('context', filters.context);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      
      const response = await axios.get(`${API}/api/notification-logs?${params.toString()}`);
      
      setLogs(response.data.logs || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/notification-logs/stats?days=30`);
      setStats(response.data);
    } catch (error) {
      // Silent fail - stats are optional
    }
  }, []);

  const fetchWhatsappAnalytics = useCallback(async () => {
    try {
      const [analyticsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/analytics`),
        axios.get(`${API}/api/whatsapp/alerts`)
      ]);
      setWhatsappAnalytics(analyticsRes.data);
      setWhatsappAlerts(alertsRes.data?.alerts || []);
    } catch (error) {
      // Silent fail - analytics are optional
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchWhatsappAnalytics();
  }, [fetchStats, fetchWhatsappAnalytics]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.context) params.append('context', filters.context);
      
      const response = await axios.get(`${API}/api/notification-logs/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs_notificacoes_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.context) params.append('context', filters.context);
      
      const response = await axios.get(`${API}/api/notification-logs/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_notificacoes_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4 text-blue-500" />;
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Send className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { color: 'bg-yellow-500', icon: Clock, label: 'Pendente' },
      enviado: { color: 'bg-blue-500', icon: Send, label: 'Enviado' },
      entregue: { color: 'bg-green-500', icon: CheckCircle, label: 'Entregue' },
      lido: { color: 'bg-emerald-600', icon: Eye, label: 'Lido' },
      clicado: { color: 'bg-purple-500', icon: CheckCircle, label: 'Clicado' },
      erro: { color: 'bg-red-500', icon: XCircle, label: 'Erro' },
      devolvido: { color: 'bg-orange-500', icon: AlertCircle, label: 'Devolvido' }
    };
    
    const config = statusConfig[status] || statusConfig.pendente;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getContextLabel = (context) => {
    const labels = {
      avaliacao: 'Avaliação',
      treinamento: 'Treinamento',
      reset_senha: 'Reset Senha',
      convite: 'Convite',
      lembrete: 'Lembrete',
      vencimento: 'Vencimento',
      geral: 'Geral'
    };
    return labels[context] || context;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      status: '',
      context: '',
      startDate: '',
      endDate: ''
    });
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="notification-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Logs de Notificações
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Auditoria de e-mails e mensagens WhatsApp enviadas
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchLogs(); fetchStats(); }}
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button
            onClick={handleExportPDF}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-slate-100 dark:bg-slate-800 p-2 rounded-lg">
                <BarChart3 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Total (30 dias)</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">E-mails</p>
                <p className="text-xl font-bold">{stats.email?.total || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 dark:bg-green-900 p-2 rounded-lg">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">WhatsApp</p>
                <p className="text-xl font-bold">{stats.whatsapp?.total || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 dark:bg-emerald-900 p-2 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Taxa de Sucesso</p>
                <p className="text-xl font-bold text-emerald-600">{stats.success_rate}%</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-red-200 dark:border-red-800">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 dark:bg-red-900 p-2 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold">Erros</p>
                <p className="text-xl font-bold text-red-600">
                  {(stats.email?.erro || 0) + (stats.whatsapp?.erro || 0)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* WhatsApp Analytics & Alerts - Discreto */}
      {whatsappAnalytics && (
        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-semibold text-green-800 dark:text-green-300">Analytics WhatsApp</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
              <span>Sucesso: <strong className="text-green-600">{whatsappAnalytics.taxas?.sucesso}%</strong></span>
              <span>Entrega: <strong className="text-blue-600">{whatsappAnalytics.taxas?.entrega}%</strong></span>
              <span>Leitura: <strong className="text-purple-600">{whatsappAnalytics.taxas?.leitura}%</strong></span>
            </div>
          </div>
          
          <div className="grid grid-cols-6 gap-3 text-center text-xs">
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">Total</p>
              <p className="font-bold text-lg">{whatsappAnalytics.total_mensagens}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">Enviadas</p>
              <p className="font-bold text-lg text-green-600">{whatsappAnalytics.enviadas}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">Entregues</p>
              <p className="font-bold text-lg text-blue-600">{whatsappAnalytics.entregues}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">Lidas</p>
              <p className="font-bold text-lg text-purple-600">{whatsappAnalytics.lidas}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">Falhas</p>
              <p className="font-bold text-lg text-red-600">{whatsappAnalytics.falhas}</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-2 rounded">
              <p className="text-slate-500">24h</p>
              <p className="font-bold text-lg">{whatsappAnalytics.ultimas_24h?.enviadas || 0}</p>
            </div>
          </div>
          
          {/* Alertas discretos */}
          {whatsappAlerts.length > 0 && (
            <div className="mt-3 space-y-2">
              {whatsappAlerts.map((alert, idx) => (
                <div 
                  key={idx} 
                  className={`p-2 rounded text-xs flex items-center gap-2 ${
                    alert.type === 'critical' ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' :
                    alert.type === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300' :
                    'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                  }`}
                >
                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                  <span><strong>{alert.title}:</strong> {alert.message}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-slate-700 dark:text-slate-300">Filtros</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Select
            value={filters.type || "all"}
            onValueChange={(value) => setFilters({ ...filters, type: value === "all" ? "" : value })}
          >
            <SelectTrigger data-testid="filter-type">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="email">E-mail</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={filters.status || "all"}
            onValueChange={(value) => setFilters({ ...filters, status: value === "all" ? "" : value })}
          >
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="enviado">Enviado</SelectItem>
              <SelectItem value="entregue">Entregue</SelectItem>
              <SelectItem value="lido">Lido</SelectItem>
              <SelectItem value="erro">Erro</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            value={filters.context || "all"}
            onValueChange={(value) => setFilters({ ...filters, context: value === "all" ? "" : value })}
          >
            <SelectTrigger data-testid="filter-context">
              <SelectValue placeholder="Contexto" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os contextos</SelectItem>
              <SelectItem value="avaliacao">Avaliação</SelectItem>
              <SelectItem value="treinamento">Treinamento</SelectItem>
              <SelectItem value="reset_senha">Reset Senha</SelectItem>
              <SelectItem value="convite">Convite</SelectItem>
              <SelectItem value="lembrete">Lembrete</SelectItem>
            </SelectContent>
          </Select>
          
          <Input
            type="date"
            placeholder="Data início"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            data-testid="filter-start-date"
          />
          
          <Input
            type="date"
            placeholder="Data fim"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            data-testid="filter-end-date"
          />
          
          <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
            Limpar Filtros
          </Button>
        </div>
      </Card>

      {/* Logs Table */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">Data/Hora</TableHead>
              <TableHead className="w-[80px]">Tipo</TableHead>
              <TableHead>Destinatário</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead>Contexto</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
                  <p className="text-slate-500">Carregando...</p>
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500">Nenhum log encontrado</p>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow 
                  key={log.id} 
                  className={log.status === 'erro' ? 'bg-red-50 dark:bg-red-950/20' : ''}
                  data-testid={`log-row-${log.id}`}
                >
                  <TableCell className="text-sm">
                    {formatDate(log.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getTypeIcon(log.type)}
                      <span className="text-xs uppercase">{log.type}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {log.recipient}
                  </TableCell>
                  <TableCell>
                    {log.recipient_name || '-'}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(log.status)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-sm">{getContextLabel(log.context)}</span>
                      {log.context_title && (
                        <p className="text-xs text-slate-500 truncate max-w-[200px]">
                          {log.context_title}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedLog(log); setShowDetail(true); }}
                      data-testid={`view-log-${log.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-slate-500">
              Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} de {total} registros
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLog && getTypeIcon(selectedLog.type)}
              Detalhes da Notificação
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 uppercase">Tipo</label>
                  <p className="font-medium">{selectedLog.type === 'email' ? 'E-mail' : 'WhatsApp'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase">Destinatário</label>
                  <p className="font-mono text-sm">{selectedLog.recipient}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase">Nome</label>
                  <p>{selectedLog.recipient_name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase">Data/Hora</label>
                  <p>{formatDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase">Contexto</label>
                  <p>{getContextLabel(selectedLog.context)}</p>
                </div>
              </div>
              
              {selectedLog.context_title && (
                <div>
                  <label className="text-xs text-slate-500 uppercase">Título do Contexto</label>
                  <p>{selectedLog.context_title}</p>
                </div>
              )}
              
              {selectedLog.message_preview && (
                <div>
                  <label className="text-xs text-slate-500 uppercase">Preview da Mensagem</label>
                  <p className="text-sm bg-slate-50 dark:bg-slate-800 p-3 rounded-lg mt-1">
                    {selectedLog.message_preview}
                  </p>
                </div>
              )}
              
              {selectedLog.external_id && (
                <div>
                  <label className="text-xs text-slate-500 uppercase">ID Externo</label>
                  <p className="font-mono text-xs">{selectedLog.external_id}</p>
                </div>
              )}
              
              {selectedLog.error_message && (
                <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                  <label className="text-xs text-red-600 uppercase font-semibold">Erro</label>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {selectedLog.error_message}
                  </p>
                </div>
              )}
              
              {selectedLog.status_history && selectedLog.status_history.length > 0 && (
                <div>
                  <label className="text-xs text-slate-500 uppercase mb-2 block">Histórico de Status</label>
                  <div className="space-y-2">
                    {selectedLog.status_history.map((h, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <span className="text-slate-400 w-36">{formatDate(h.timestamp)}</span>
                        {getStatusBadge(h.status)}
                        <span className="text-slate-500 text-xs">({h.source})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationLogs;
